import type { ReceiptSplitPlan } from "../../types/receipt";
import { mooReceiptForAction } from "../../lib/moo-receipts";
import { lazy, Suspense, type Dispatch, type SetStateAction } from "react";
import { ArrowUpRight } from "lucide-react";
import type { DashboardData, ExpenseInput, Owner } from "../../types";
import type { MooAction, MooMessage } from "../../types/moo";
import ExpensePreview from "./MooExpensePreview";
import { mooEntryKey } from "./moo-entry-key";

const MooPricePreview = lazy(() => import("./MooPricePreview"));

/** What an action does, as its heading in the proposal. */
export function actionTitle(a: MooAction) {
  const type = a.expense?.type || a.expected?.type;
  const isTransfer = type === "Transfer" || type === "Card repayment" || !!a.expected?.toAccountId || !!a.expense?.toAccountId;
  const isIncome = type === "Income";
  const noun = isTransfer ? "transfer" : isIncome ? "income" : "expense";
  const titles: Record<string, string> = {
    expense_add: `New ${noun}`,
    expense_edit: `Edit ${noun}`,
    expense_delete: `Delete ${noun}`,
    price_add: "Track a price",
    price_edit: "Edit tracked price",
    price_delete: "Delete tracked price",
    category_add: `New ${a.category?.type === "Income" ? "income " : ""}${a.category?.name ? "category" : "subcategory"}`,
    category_rename: "Rename category",
    category_merge: "Merge categories",
    payee_add: "New payee",
    payee_edit: "Edit payee",
    payee_remove: "Remove payee",
    loan_create: "New loan",
    loan_settle: "Record repayment",
    loan_edit: "Edit loan",
    loan_write_off: "Write off loan",
    loan_delete: "Delete loan",
    loan_payment_edit: "Edit repayment",
    loan_payment_delete: "Delete repayment",
    bill_create: (a.bill?.type || a.expectedBill?.type) === "Income" ? "New recurring income" : "New bill",
    bill_edit: "Edit bill",
    bill_archive: "Archive bill",
    bill_pay: (a.bill?.type || a.expectedBill?.type) === "Income" ? "Mark income received" : "Pay bill",
    bill_skip: "Skip bill",
    budget_set: a.budget?.amount ? "Set budget" : "Clear budget",
    trip_create: "New trip",
    trip_edit: "Edit trip",
    trip_archive: "Archive trip",
    memory_save: a.id ? "Update memory" : "Remember rule",
    memory_forget: "Forget rule",
    account_create: "New account",
    account_edit: "Edit account",
    account_archive: "Archive account",
    reconciliation_confirm: "Reconcile account",
    reconciliation_complete: "Complete reconciliation",
    smart_rule_create: "New smart rule",
    smart_rule_edit: "Edit smart rule",
    smart_rule_delete: "Delete smart rule",
  };
  return titles[a.kind];
}

/** One change in a Moo proposal: a preview of it, editable where the kind allows. */
export default function MooProposedAction({ m, a, index, data, profile, busy, revise, setEditing, setError, openTransaction }: {
  m: MooMessage;
  a: MooAction;
  index: number;
  data: DashboardData;
  profile: Owner;
  busy: string;
  revise: (m: MooMessage, index: number, expense?: ExpenseInput, item?: MooAction["item"], remove?: boolean, receiptSplit?: ReceiptSplitPlan, repairLinePrices?: boolean) => Promise<void>;
  setEditing: Dispatch<SetStateAction<Record<string, boolean>>>;
  setError: (message: string) => void;
  openTransaction: (id: string) => void;
}) {
  const removeButton = m.payload.status === "pending" && (
    <button className="moo-edit" disabled={!!busy} onClick={() => void revise(m, index, undefined, undefined, true).catch((e) => setError(e.message))}>Remove from proposal</button>
  );
  return (
    <>
      <h4>{actionTitle(a)}</h4>
      {a.expense ? (
        <ExpensePreview
          action={a}
          receipt={
            mooReceiptForAction(m.payload, index)
          }
          data={data}
          profile={profile}
          disabled={
            !!busy || m.payload.status !== "pending"
          }
          onRevise={(e, receiptSplit, repairLinePrices) => revise(m, index, e, undefined, undefined, receiptSplit, repairLinePrices)}
          onEditing={(value) =>
            setEditing((v) => ({
              ...v,
              [mooEntryKey(m.id, a, index)]: value,
            }))
          }
        />
      ) : a.payee || a.expectedPayee ? (
        <div className="moo-action-info">
          {removeButton}
          <strong>{a.payee?.name || a.expectedPayee?.name}</strong>
          <p>{a.kind === "payee_add" ? "Create this payee." : a.kind === "payee_edit" ? `Update ${a.expectedPayee?.name}.` : "Remove this payee; Barnyard will archive it if financial history still refers to it."}</p>
          {a.payee?.notes && <p>{a.payee.notes}</p>}
        </div>
      ) : a.loan || a.expectedLoan ? (
        <div className="moo-action-info">
          {removeButton}
          <strong>{a.loan?.description || a.expectedLoan?.description || "Loan repayment"}</strong>
          <p>
            {a.kind === "loan_create" && `${a.loan?.originalAmount} · ${a.loan?.direction} · ${a.loan?.createdDate}`}
            {a.kind === "loan_settle" && `${a.loan?.amount} · ${a.loan?.date}`}
            {a.kind === "loan_edit" && `Change the loan to ${a.loan?.originalAmount}.`}
            {a.kind === "loan_write_off" && `Close the outstanding ${a.expectedLoan?.outstanding} ${a.expectedLoan?.currency} without a payment.`}
            {a.kind === "loan_delete" && "Delete the loan, its repayments and every cash transaction behind them."}
            {a.kind === "loan_payment_edit" && `Change the repayment to ${a.loan?.amount} on ${a.loan?.date}.`}
            {a.kind === "loan_payment_delete" && "Delete this repayment and its cash transaction."}
          </p>
        </div>
      ) : a.budget ? (
        <div className="moo-action-info">
          {removeButton}
          <strong>{a.budget.owner} · {a.budget.category || "Overall"}</strong>
          <p>
            {a.budget.amount
              ? `${a.budget.amount} ${a.budget.currency} from ${a.budget.month}${a.budget.repeats ? ", every month after" : ", this month only"}.`
              : `Stop budgeting this from ${a.budget.month}.`}
          </p>
        </div>
      ) : a.bill || a.expectedBill ? (
        <div className="moo-action-info">
          {removeButton}
          <strong>{a.bill?.name || a.expectedBill?.name || "Bill"}</strong>
          <p>
            {a.kind === "bill_create" && (a.bill?.sourceTransactionId
              ? `${a.bill?.frequency} · anchored to an existing transaction as already paid`
              : `${a.bill?.amount ? `${a.bill.amount} ${a.bill.currency} · ` : "no fixed amount · "}${a.bill?.frequency} · starts ${a.bill?.startDate}`)}
            {a.kind === "bill_edit" && `Change the bill to ${a.bill?.amount ? `${a.bill.amount} ${a.bill.currency}` : "no fixed amount"}, ${a.bill?.frequency}.`}
            {a.kind === "bill_archive" && "Archive this bill; its payment history stays linked."}
            {a.kind === "bill_skip" && `Skip the cycle due ${a.expectedBill?.nextDue}; no transaction is created.`}
          </p>
        </div>
      ) : a.trip || a.expectedTrip ? (
        <div className="moo-action-info">
          {removeButton}
          <strong>{a.trip?.name || a.expectedTrip?.name || "Trip"}</strong>
          <p>
            {a.kind === "trip_create" && `${a.trip?.destinations?.length ? a.trip.destinations.join(", ") + " · " : ""}${a.trip?.startDate} to ${a.trip?.endDate}${a.trip?.budget ? ` · Budget: ${a.trip.budget} ${a.trip.budgetCurrency || ""}` : ""}`}
            {a.kind === "trip_edit" && `Update trip dates to ${a.trip?.startDate} to ${a.trip?.endDate}${a.trip?.budget ? ` · Budget: ${a.trip.budget} ${a.trip.budgetCurrency || ""}` : ""}.`}
            {a.kind === "trip_archive" && "Archive this trip; past trip expenses keep their links."}
          </p>
        </div>
      ) : a.category ? (
        <div className="moo-action-info">
          {removeButton}
          {a.kind === "category_add" &&
            (a.category.name ? (
              <p>
                Create <strong>{a.category.name}</strong>
                {a.category.subcategory
                  ? ` with starting subcategory "${a.category.subcategory}"`
                  : ""}
                .
              </p>
            ) : (
              <p>
                Add subcategory{" "}
                <strong>{a.category.subcategory}</strong>{" "}
                under {a.category.target}.
              </p>
            ))}
          {a.kind === "category_rename" && (
            <p>
              Rename <strong>{a.category.target}</strong>{" "}
              to <strong>{a.category.name}</strong>.
              Existing transactions using it will be
              reassigned.
            </p>
          )}
          {a.kind === "category_merge" && (
            <p>
              Merge <strong>{a.category.target}</strong>{" "}
              into{" "}
              <strong>{a.category.destination}</strong>.
              Its transactions and linked prices will
              be reassigned.
            </p>
          )}
          <small>
            Describe any corrections in the chat.
          </small>
        </div>
      ) : a.memory ? (
        <div className="moo-action-info">
          {removeButton}
          <strong>{a.kind === "memory_forget" ? "Forget rule" : a.memory.topic || "Rule"}</strong>
          <p>{a.memory.memory}</p>
          {a.memory.profile && <p>For {a.memory.profile}</p>}
          <small>Permanent memory rule across conversations.</small>
        </div>
      ) : a.account ? (
        <div className="moo-action-info">
          {removeButton}
          <strong>{a.account.name}</strong>
          <p>{a.account.owner} · {a.account.type} · {a.account.currency}</p>
          {a.account.openingBalance && <p>Opening balance: {a.account.openingBalance} {a.account.currency}</p>}
          {a.kind === "account_archive" && <p>Transactions will remain preserved in history.</p>}
          <small>Describe any corrections in the chat.</small>
        </div>
      ) : a.reconciliation ? (
        <div className="moo-action-info">
          {removeButton}
          <strong>{a.kind === "reconciliation_complete" ? `Close ${a.reconciliation.month}` : `Reconcile ${data.accounts.find(acc => acc.id === a.reconciliation?.accountId)?.name || a.reconciliation.accountId}`}</strong>
          <p>Month: {a.reconciliation.month}</p>
          {a.reconciliation.confirmedBalance && <p>Closing balance: {a.reconciliation.confirmedBalance}</p>}
          {a.reconciliation.note && <p>Note: {a.reconciliation.note}</p>}
          <small>Describe any corrections in the chat.</small>
        </div>
      ) : a.rule ? (
        <div className="moo-action-info">
          {removeButton}
          <strong>{a.rule.name}</strong>
          <p>
            {a.kind === "smart_rule_delete"
              ? "Delete this smart rule."
              : `Type: ${a.rule.kind || "sms"}${a.rule.priority ? ` · Priority: ${a.rule.priority}` : ""}`}
          </p>
          {a.kind !== "smart_rule_delete" && (
            <>
              {a.rule.conditions.bankAccount && <p>Sending bank identifier: <strong>{a.rule.conditions.bankAccount}</strong></p>}
              {a.rule.conditions.recipient && <p>Exact recipient: <strong>{a.rule.conditions.recipient}</strong></p>}
              {a.rule.conditions.cardSuffix && <p>When card ends with: <strong>{a.rule.conditions.cardSuffix}</strong></p>}
              {a.rule.conditions.merchantPattern && <p>When merchant contains: <strong>{a.rule.conditions.merchantPattern}</strong></p>}
              {a.rule.conditions.sender && <p>Sender: {a.rule.conditions.sender}</p>}
              {a.rule.actions.accountId && (
                <p>Assign account: <strong>{data.accounts.find((acc) => acc.id === a.rule?.actions.accountId)?.name || a.rule.actions.accountId}</strong></p>
              )}
              {a.rule.actions.category && (
                <p>Assign category: <strong>{a.rule.actions.category}{a.rule.actions.subcategory ? ` / ${a.rule.actions.subcategory}` : ""}</strong></p>
              )}
              {a.rule.actions.title && <p>Title: <strong>{a.rule.actions.title}</strong></p>}
              {a.rule.actions.payeeName && <p>Payee: <strong>{a.rule.actions.payeeName}</strong></p>}
              {a.rule.actions.autoApprove && <p>Auto-approve: Yes</p>}
            </>
          )}
          <small>Describe any corrections in the chat.</small>
        </div>
      ) : (
        <div className="moo-action-info">
          {m.payload.status === "pending" && a.item && <Suspense fallback={<small>Loading price details…</small>}><MooPricePreview payees={data.payees || []} item={a.item} disabled={!!busy} onSave={item => revise(m, index, undefined, item)} onEditing={value => setEditing(v => ({...v, [`${m.id}:${index}`]: value}))} /></Suspense>}
          {removeButton}
          <strong>
            {a.item?.name ||
              a.priorPrice?.name ||
              a.expected?.title}
          </strong>
          <p>
            {a.item
              ? `${a.item.total} ${a.item.currency} · ${a.item.date} · ${a.item.payeeName}${a.item.measure ? " · " + a.item.measure + " " + a.item.unit : ""}`
              : a.expected
                ? `${a.expected.amount} ${a.expected.currency} · ${a.expected.date}`
                : `${a.priorPrice?.total} ${a.priorPrice?.currency} · ${a.priorPrice?.payeeName}`}
          </p>
          {a.item && (
            <p>
              Brand: {a.item.brand || "Not specified"}{" "}
              · Group: {a.item.group}
            </p>
          )}
          {a.item?.pack && <p>Pack: {a.item.pack}</p>}
          {a.item?.notes && <p>{a.item.notes}</p>}
          {a.item && (
            <p>
              {a.item.transactionId
                ? `Source: ${a.item.transactionId}`
                : "Standalone price"}
            </p>
          )}
          {a.kind === "expense_delete" && (
            <p>
              Linked prices will be kept as standalone
              observations.
            </p>
          )}
          <small>
            Describe any corrections in the chat.
          </small>
        </div>
      )}
      {m.payload.status === "saved" &&
        (a.kind === "bill_pay" ||
          (a.kind.startsWith("expense") && a.kind !== "expense_delete")) && (
          <button
            className="moo-link"
            onClick={() =>
              openTransaction(
                m.payload.completed?.[
                  String(index)
                ] || a.id,
              )
            }
          >
            Open transaction{" "}
            <ArrowUpRight size={16} />
          </button>
        )}
    </>
  );
}
